import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  HiOutlineCreditCard,
  HiOutlineSparkles,
  HiOutlineX,
} from "react-icons/hi";
import CustomButton from "components/common/Button/CustomButton";
import { releaseBodyScrollLockAfterTransition } from "../utils/bodyScrollLock";
import { buildTimesheetRowLabel } from "../utils/timesheetAiUtils";

const formatAud = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
};

const TimesheetAiConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  timesheetCount = 0,
  timesheets = [],
  balanceAud = null,
  walletLoading = false,
  walletError = null,
  confirming = false,
  variant = "batch",
}) => {
  const count = Math.max(0, Number(timesheetCount) || 0);
  const isSingle = variant === "single" || count === 1;
  const firstRow = timesheets?.[0];
  const { title: firstTitle } = buildTimesheetRowLabel(firstRow);

  const confirmMessage = isSingle
    ? `Are you sure you want to analyse${firstTitle ? ` ${firstTitle}'s timesheet` : " this timesheet"} with AI?`
    : `Are you sure you want to analyse ${count} selected timesheet${count === 1 ? "" : "s"} with AI?`;

  const balance =
    balanceAud == null ? null : Number(balanceAud);
  const walletUnavailable = Boolean(walletError);
  const walletReady = !walletLoading && balanceAud != null && !walletUnavailable;
  const emptyBalance = walletReady && balance <= 0;
  const confirmDisabled =
    confirming || walletLoading || walletUnavailable || emptyBalance;

  const handleClose = () => {
    if (!confirming) onClose?.();
  };

  const content = (
    <Transition show={isOpen} as={Fragment} afterLeave={releaseBodyScrollLockAfterTransition}>
      <Dialog
        as='div'
        className='relative z-[10090]'
        open={isOpen}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-200'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-150'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-slate-900/50 backdrop-blur-[2px]' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto p-4 sm:p-6'>
          <div className='flex min-h-full items-center justify-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-200'
              enterFrom='opacity-0 translate-y-2 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-150'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-2 sm:scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl ring-1 ring-black/5 transition-all'>
                <div className='flex items-center justify-between gap-3 bg-gradient px-5 py-4 text-white'>
                  <div className='flex min-w-0 items-center gap-3'>
                    <span className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-inset ring-white/20'>
                      <HiOutlineSparkles className='h-5 w-5' />
                    </span>
                    <Dialog.Title
                      as='h2'
                      className='text-base font-semibold leading-snug sm:text-lg'
                    >
                      Confirm AI Analyse
                    </Dialog.Title>
                  </div>
                  <button
                    type='button'
                    onClick={handleClose}
                    disabled={confirming}
                    className='rounded-lg p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white disabled:opacity-40'
                    aria-label='Close'
                  >
                    <HiOutlineX className='h-5 w-5' />
                  </button>
                </div>

                <div className='px-5 py-5 sm:px-6'>
                  <p className='text-sm leading-relaxed text-gray-600'>
                    {confirmMessage}
                  </p>

                  <div className='mt-4 flex h-11 items-center justify-between gap-4 rounded-xl border border-blue-100 bg-[#ECF0FD]/70 px-4'>
                    <div className='flex min-w-0 items-center gap-2.5'>
                      <HiOutlineCreditCard className='h-5 w-5 shrink-0 text-[#4B6CB7]' />
                      <span className='text-sm font-medium leading-none text-[#02397A]'>
                        AI credit balance
                      </span>
                    </div>
                    <div className='flex shrink-0 items-center'>
                      {walletLoading ? (
                        <span className='inline-flex items-center gap-2 text-sm leading-none text-[#4B6CB7]'>
                          <span className='inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-[#4B6CB7]' />
                          Loading…
                        </span>
                      ) : walletUnavailable ? (
                        <span className='text-sm font-semibold leading-none tabular-nums text-[#02397A]'>
                          {formatAud(0)}
                        </span>
                      ) : walletReady ? (
                        <span className='text-sm font-semibold leading-none tabular-nums text-[#02397A]'>
                          {formatAud(balance)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {emptyBalance ? (
                    <p className='mt-3 text-xs font-medium text-red-600'>
                      Insufficient credits. Top up in Settings to continue.
                    </p>
                  ) : null}
                </div>

                <div className='flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:px-6'>
                  <CustomButton
                    type='button'
                    variant='secondary'
                    label='Cancel'
                    clickHandler={handleClose}
                    isDisabled={confirming}
                    cssClass='!min-w-[6.5rem] justify-center'
                  />
                  <CustomButton
                    type='button'
                    variant='primary'
                    label={confirming ? "Analysing…" : "Yes, analyse"}
                    Icon={!confirming ? <HiOutlineSparkles className='h-4 w-4' /> : null}
                    clickHandler={onConfirm}
                    isDisabled={confirmDisabled}
                    showLoading={confirming}
                    cssClass='!min-w-[6.5rem] justify-center'
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return createPortal(content, document.body);
};

export default TimesheetAiConfirmModal;
